import React from 'react';
import { MasterPlanListByGroupItem, MasterPlanListStatusItem } from 'types/dacum/masterPlan';
import BadgeStatus from 'components/Badge/BadgeStatus';
import { StatusType, getStatusColor, getStatusLabel } from 'constant';
import {
  Badge,
  Box,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCalendar, IconChevronRight, IconClipboardList, IconUsers } from '@tabler/icons-react';

// Build a compact summary: { Approved: 2, Pending: 1, ... }
const buildSummary = (list: MasterPlanListStatusItem[]) => {
  const map: Record<string, number> = {};
  list.forEach((item) => {
    const key = item.status || 'Draft';
    map[key] = (map[key] || 0) + 1;
  });
  return Object.entries(map);
};

const StatusCell: React.FC<{ row: MasterPlanListByGroupItem }> = ({ row }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const list = row.listStatus;
  const hasMultiple = list && list.length > 0;
  const overallStatus = row.status;

  const summary = hasMultiple ? buildSummary(list) : [];
  // Up to 4 unique status colors for the dot preview
  const dotColors = summary
    .slice(0, 4)
    .map(([s]) => getStatusColor(s.toLowerCase() as StatusType) || '#9AA1B1');

  return (
    <>
      {hasMultiple ? (
        <UnstyledButton onClick={open}>
          <Group
            gap={7}
            wrap='nowrap'
            px={10}
            py={5}
            style={{
              borderRadius: 999,
              background: 'rgba(59,130,246,0.07)',
              border: '1px solid rgba(59,130,246,0.18)',
              transition: 'background 0.15s',
              cursor: 'pointer'
            }}
          >
            {/* Color dot strip */}
            <Group gap={3} wrap='nowrap'>
              {dotColors.map((color, i) => (
                <Box
                  key={i}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0
                  }}
                />
              ))}
            </Group>
            <Text size='xs' fw={600} c='blue.6' style={{ whiteSpace: 'nowrap' }}>
              {list.length} plan{list.length !== 1 ? 's' : ''}
            </Text>
            <IconChevronRight size={11} color='#3b82f6' />
          </Group>
        </UnstyledButton>
      ) : overallStatus ? (
        <BadgeStatus status={overallStatus.toLowerCase() as StatusType} />
      ) : (
        <Text size='xs' c='dimmed'>
          —
        </Text>
      )}

      <Modal
        opened={opened}
        onClose={close}
        size='lg'
        centered
        radius='lg'
        padding='xl'
        title={
          <Group gap={12} wrap='nowrap'>
            <ThemeIcon size={40} radius='md' variant='light' color='blue'>
              <IconClipboardList size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={700} fz={16} lh={1.3}>
                Masterplan Status
              </Text>
              <Group gap={6} wrap='nowrap'>
                <IconUsers size={12} color='#9ca3af' />
                <Text size='xs' c='dimmed' style={{ whiteSpace: 'nowrap' }}>
                  {row.jobFamGroup || '—'}
                </Text>
                <Text size='xs' c='dimmed'>
                  ·
                </Text>
                <IconCalendar size={12} color='#9ca3af' />
                <Text size='xs' c='dimmed' style={{ whiteSpace: 'nowrap' }}>
                  {row.year}
                  {row.endYear ? `–${row.endYear}` : ''}
                </Text>
              </Group>
            </Stack>
          </Group>
        }
      >
        {/* Summary row */}
        <Group gap={6} mb={4} wrap='wrap'>
          {summary.map(([status, count]) => {
            const color = getStatusColor(status.toLowerCase() as StatusType) || '#9AA1B1';
            return (
              <Group
                key={status}
                gap={5}
                px={10}
                py={4}
                wrap='nowrap'
                style={{
                  borderRadius: 999,
                  background: `${color}15`,
                  border: `1px solid ${color}40`
                }}
              >
                <Box
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0
                  }}
                />
                <Text size='xs' fw={700} style={{ color }}>
                  {count}
                </Text>
                <Text size='xs' fw={500} c='dimmed'>
                  {status}
                </Text>
              </Group>
            );
          })}
        </Group>

        <Divider my='md' />

        {/* Item list */}
        <ScrollArea.Autosize mah={380} offsetScrollbars>
          <Stack gap={6}>
            {list?.map((item: MasterPlanListStatusItem, idx: number) => {
              const statusKey = (item.status || 'draft').toLowerCase() as StatusType;
              const accentColor = getStatusColor(statusKey) || '#9AA1B1';
              return (
                <Box
                  key={idx}
                  px={14}
                  py={11}
                  style={{
                    borderRadius: 10,
                    background: `${accentColor}08`,
                    border: `1px solid ${accentColor}25`,
                    borderLeft: `3px solid ${accentColor}`
                  }}
                >
                  <Group justify='space-between' wrap='nowrap' align='center' gap={12}>
                    <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
                      <Text size='sm' fw={600} style={{ wordBreak: 'break-word', lineHeight: 1.4 }}>
                        {item.masterplanName || '—'}
                      </Text>
                      {(item.jobFamilyName || item.jobBandName) && (
                        <Group gap={4} wrap='wrap'>
                          {item.jobFamilyName && (
                            <Badge size='xs' variant='light' color='blue' radius='xl'>
                              {item.jobFamilyName}
                            </Badge>
                          )}
                          {item.jobBandName && (
                            <Badge size='xs' variant='light' color='violet' radius='xl'>
                              {item.jobBandName}
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Stack>

                    <Box
                      px={9}
                      py={4}
                      style={{
                        borderRadius: 999,
                        background: `${accentColor}18`,
                        border: `1px solid ${accentColor}45`,
                        flexShrink: 0
                      }}
                    >
                      <Text size='xs' fw={700} style={{ color: accentColor, whiteSpace: 'nowrap' }}>
                        {getStatusLabel(statusKey) || item.status || 'Draft'}
                      </Text>
                    </Box>
                  </Group>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Modal>
    </>
  );
};

export const columns = () => [
  {
    heading: 'Job Family Group',
    props: (row: MasterPlanListByGroupItem) => row.jobFamGroup || '-',
    sortable: true,
    active: true,
    disabled: false,
    id: 'jobFamGroup'
  },
  {
    heading: 'Year',
    props: (row: MasterPlanListByGroupItem) => row.year?.toString() || '-',
    sortable: true,
    active: true,
    disabled: false,
    id: 'year'
  },
  {
    heading: 'End Year',
    props: (row: MasterPlanListByGroupItem) => row.endYear?.toString() || '-',
    sortable: true,
    active: true,
    disabled: false,
    id: 'endYear'
  },
  {
    heading: 'Status',
    props: (row: MasterPlanListByGroupItem) => <StatusCell row={row} />,
    sortable: false,
    active: true,
    disabled: false,
    id: 'status'
  },
  {
    heading: 'Action',
    props: 'action',
    sortable: false,
    active: true,
    disabled: false,
    id: 'action'
  }
];
